/**
 * ContentListCard displays a list of content items in a card format.
 * Each item has an optional gradient header, markdown body, optional step entries, and action CTAs.
 * Designed for report experiences like Key Findings, Recommendations, Action Items, etc.
 *
 * ## Icon presets (for steps)
 *
 * | Name        | Appearance                         |
 * |-------------|------------------------------------|
 * | `sparkle`   | ✨ sparkle emoji                   |
 * | `success`   | Green filled dot                   |
 * | `info`      | Blue filled dot                    |
 * | `warning`   | Yellow filled dot                  |
 * | *(custom)*  | Pass any `React.ReactNode` as icon |
 * | *(none)*    | Falls back to variant dot or gray  |
 */
import React from 'react';
import { cn, Button } from '@strivelabs/ui-core';
import { RichTextRenderer } from '../rich-text';
import { titleSizes } from './blocks/CardHeader';

export interface ContentListStepEntry {
  /** Icon to display — use a preset name or pass a custom ReactNode */
  icon?: 'sparkle' | 'success' | 'info' | 'warning' | React.ReactNode;
  /** Text content (supports markdown) */
  text: string;
  /** Visual variant for the step dot/icon — used as fallback when icon is not set */
  variant?: 'default' | 'success' | 'warning' | 'info';
}

export interface ContentListAction {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Button visual style — 'default' renders cardAction, 'primary' renders gradient */
  variant?: 'default' | 'primary';
}

export interface ContentListItem {
  /** Unique identifier */
  id: string;
  /** Header text displayed in the gradient strip (supports markdown) */
  title: string;
  /** Markdown content rendered in the body */
  content?: string;
  /** Optional step entries shown as a bulleted list between content and actions */
  steps?: ContentListStepEntry[];
  /** Optional action buttons at the bottom */
  actions?: ContentListAction[];
  /**
   * Optional rendered node placed at the bottom of the item, after `actions`.
   * Use this slot to embed custom CTAs (e.g. an inline chat invitation) per item.
   */
  footer?: React.ReactNode;
}

export interface ContentListCardProps {
  /** Card heading rendered above the item list */
  title?: string;
  /** Size of the card heading */
  titleSize?: 'sm' | 'md' | 'lg' | 'customXl';
  /** The list of items to render */
  items: ContentListItem[];
  /** Show the steps section for items that have step entries (default: true) */
  showSteps?: boolean;
  /** Background color applied to the outer wrapper — pair with `className` to add padding/border if needed */
  backgroundColor?: string;
  /** Additional CSS classes for the outer wrapper */
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Preset icons for step entries                                      */
/*                                                                     */
/*  sparkle  → ✨ emoji                                                */
/*  success  → green filled dot                                        */
/*  info     → blue filled dot                                         */
/*  warning  → yellow filled dot                                       */
/*  (custom) → any React.ReactNode passed as `icon`                    */
/*  (none)   → falls back to variant-based dot or gray dot             */
/* ------------------------------------------------------------------ */

const SparkleIcon = () => (
  <span className="text-sm leading-none">&#10024;</span>
);

const SuccessDot = () => (
  <span className="inline-block size-2.5 rounded-full bg-green-500" />
);

const InfoDot = () => (
  <span className="inline-block size-2.5 rounded-full bg-blue-500" />
);

const WarningDot = () => (
  <span className="inline-block size-2.5 rounded-full bg-yellow-500" />
);

function resolveIcon(
  icon: ContentListStepEntry['icon'],
  variant: ContentListStepEntry['variant']
) {
  // Custom ReactNode
  if (icon && typeof icon !== 'string') return icon;

  // Named presets
  if (icon === 'sparkle') return <SparkleIcon />;
  if (icon === 'success') return <SuccessDot />;
  if (icon === 'info') return <InfoDot />;
  if (icon === 'warning') return <WarningDot />;

  // Fallback to variant-based dot
  if (variant === 'success') return <SuccessDot />;
  if (variant === 'warning') return <WarningDot />;
  if (variant === 'info') return <InfoDot />;

  return <span className="inline-block size-2.5 rounded-full bg-gray-300" />;
}

/* ------------------------------------------------------------------ */
/*  Theme palette                                                      */
/* ------------------------------------------------------------------ */

const theme = {
  titleBorder: 'border-rose-200/40 dark:border-rose-900/40',
  cardHover: 'hover:shadow-md hover:border-rose-200/40 dark:hover:border-rose-900/50',
  headerGradient:
    'border-orange-100/90 bg-gradient-to-r from-orange-50 via-amber-50/80 to-orange-50/40 dark:border-orange-950/40 dark:from-orange-950/40 dark:via-amber-950/25 dark:to-orange-950/20',
  stepHover: 'hover:bg-orange-50/60 dark:hover:bg-orange-950/25',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ContentListCard: React.FC<ContentListCardProps> = ({
  title,
  titleSize = 'customXl',
  items,
  showSteps = true,
  backgroundColor,
  className,
}) => {
  return (
    <div
      className={cn('w-full', className)}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {/* Card title + underline */}
      {title && (
        <h3 className={cn(titleSizes[titleSize], 'border-b pb-2 mb-5', theme.titleBorder)}>{title}</h3>
      )}

      {/* Items */}
      <div className="space-y-4">
        {items.map((item) => (
          <div
            key={item.id}
            className={cn('group/card rounded-xl border-[0.5px] border-custom overflow-hidden bg-custom-card-bg shadow-sm transition-all duration-300 ease-out', theme.cardHover)}
          >
            {/* Gradient header */}
            <div className={cn('border-b px-4 py-3.5', theme.headerGradient)}>
              <span className="text-sm font-semibold leading-snug text-custom-text-primary flex-1">
                <RichTextRenderer content={item.title} />
              </span>
            </div>

            {/* Body */}
            {(item.content || (showSteps && item.steps?.length) || item.actions?.length || item.footer) && (
              <div className="px-4 py-4 space-y-4">
                {/* Markdown content */}
                {item.content && (
                  <div className="text-sm leading-relaxed text-custom-text-primary">
                    <RichTextRenderer content={item.content} />
                  </div>
                )}

                {/* Steps */}
                {showSteps && item.steps && item.steps.length > 0 && (
                  <div className="space-y-1">
                    {item.steps.map((entry, idx) => (
                      <div key={idx} className={cn('group/step flex items-start gap-3 rounded-lg px-2 py-2 transition-colors', theme.stepHover)}>
                        <span className="mt-0.5 shrink-0 flex items-center justify-center w-4 transition-transform duration-200 group-hover/step:scale-110">
                          {resolveIcon(entry.icon, entry.variant)}
                        </span>
                        <span className="text-sm text-custom-text-primary leading-relaxed">
                          <RichTextRenderer content={entry.text} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action CTAs */}
                {item.actions && item.actions.length > 0 && (
                  <div className="flex items-center justify-end gap-2">
                    {item.actions.map((action, idx) => (
                      <Button
                        key={idx}
                        variant={action.variant === 'primary' ? 'gradient' : 'cardAction'}
                        size="xs"
                        onClick={action.onClick}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Custom footer slot (e.g. embedded chat CTA) */}
                {item.footer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
